import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bed, Bath, Maximize, MapPin, Phone, User, ArrowLeft, ChevronLeft, ChevronRight, MessageCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './PropertyDetail.css';

const PropertyDetail = ({ property, onBack }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    image,
    allImages = [],
    title,
    listingType,
    price,
    city,
    district,
    details,
    ai_aciklama,
    agent
  } = property;

  // Use a stable images array with unique values to prevent 'skipping' feel if duplicates exist
  const images = React.useMemo(() => {
    const combined = allImages.length > 0 ? allImages : [image];
    return [...new Set(combined)];
  }, [allImages, image]);

  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const thumbnailsRef = useRef(null);
  const lightboxThumbnailsRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleNext = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (images.length <= 1) return;
    setSelectedImageIdx((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const handlePrev = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (images.length <= 1) return;
    setSelectedImageIdx((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);
  const isSale = (listingType || '').toLowerCase().includes('sat');

  // Sync thumbnail scroll when active index changes
  useEffect(() => {
    const syncScroll = (ref) => {
      if (ref.current) {
        const activeThumbnail = ref.current.querySelector('.thumbnail-item.active');
        if (activeThumbnail) {
          activeThumbnail.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    };

    syncScroll(thumbnailsRef);
    if (isLightboxOpen) {
      syncScroll(lightboxThumbnailsRef);
    }
  }, [selectedImageIdx, isLightboxOpen]);

  // Breadcrumb/Path
  const locationPath = `${city}${district ? ' / ' : ''}${district}`;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="property-detail-container"
    >
      <div className="detail-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={18} />
          <span>Listeye Dön</span>
        </button>
      </div>

      <div className="detail-grid">
        <div className="detail-main">
          {/* Image Gallery */}
          <div className="image-gallery">
            <div className="main-image-container" onClick={() => setIsLightboxOpen(true)}>
              <AnimatePresence mode="wait">
                <motion.img 
                  key={images[selectedImageIdx]}
                  initial={{ x: 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -100, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  drag={isMobile ? "x" : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.2}
                  onDragEnd={(e, { offset, velocity }) => {
                    const swipe = offset.x;
                    if (swipe < -50) {
                      handleNext();
                    } else if (swipe > 50) {
                      handlePrev();
                    }
                  }}
                  src={images[selectedImageIdx]} 
                  alt={title} 
                  className="main-image" 
                />
              </AnimatePresence>
              
              <button className="gallery-action-btn expand-btn">
                <Maximize size={20} />
              </button>

              {images.length > 1 && (
                <>
                  <button className="gallery-nav-btn prev mobile-hidden" onClick={handlePrev}>
                    <ChevronLeft size={24} />
                  </button>
                  <button className="gallery-nav-btn next mobile-hidden" onClick={handleNext}>
                    <ChevronRight size={24} />
                  </button>
                </>
              )}

              <div className="image-counter">
                {selectedImageIdx + 1} / {images.length}
              </div>
            </div>
            {images.length > 1 && (
              <div className="thumbnails-container" ref={thumbnailsRef}>
                {images.map((img, idx) => (
                  <div 
                    key={idx} 
                    className={`thumbnail-item ${idx === selectedImageIdx ? 'active' : ''}`}
                    onClick={() => setSelectedImageIdx(idx)}
                  >
                    <img src={img} alt={`${title} ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Property Info */}
          <div className="property-main-info">
            <div className="top-badges">
              <span className={`detail-badge ${isSale ? 'badge-sale' : 'badge-rent'}`}>
                {listingType}
              </span>
              <span className="detail-badge badge-code">İlan No: {property.code || '-'}</span>
            </div>
            <h1 className="detail-title">{title}</h1>
            <div className="detail-location">
              <MapPin size={20} />
              <span>{locationPath}</span>
            </div>
            <div className="detail-price">{price}</div>

            <div className="detail-specs">
              <div className="detail-spec-item">
                <Bed size={24} />
                <div className="detail-spec-info">
                  <span className="spec-value">{details.odaSayisi || '-'}</span>
                  <span className="spec-label">Oda Sayısı</span>
                </div>
              </div>
              <div className="detail-spec-item">
                <Bath size={24} />
                <div className="detail-spec-info">
                  <span className="spec-value">{details.banyoSayisi || '-'}</span>
                  <span className="spec-label">Banyo</span>
                </div>
              </div>
              <div className="detail-spec-item">
                <Maximize size={24} />
                <div className="detail-spec-info">
                  <span className="spec-value">{details.metreKare || '-'}</span>
                  <span className="spec-label">m²</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="detail-section">
            <h2 className="section-title">Açıklama</h2>
            <div className="ai-summary">
              {ai_aciklama || 'Bu ilan için açıklama bulunmamaktadır.'}
            </div>
          </div>

          {/* Features */}
          <div className="detail-section">
            <h2 className="section-title">Emlak Bilgileri</h2>
            <div className="attributes-grid">
                {Object.entries(details).map(([key, value]) => (
                    value && (
                        <div key={key} className="attribute-item">
                            <span className="attr-label">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span>
                            <span className="attr-value">{value}</span>
                        </div>
                    )
                ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="detail-sidebar">
          <div className="agent-card">
            <h3 className="sidebar-title">Danışman</h3>
            <div className="agent-info">
              {agent.photo ? (
                <img src={agent.photo} alt={agent.name} className="agent-photo" />
              ) : (
                <div className="agent-photo-placeholder"><User size={40} /></div>
              )}
              <div className="agent-text">
                <span className="agent-name">{agent.name || 'Hasan Korkmaz'}</span>
                <span className="agent-phone-info">{agent.phone || '+90 505 214 91 97'}</span>
              </div>
            </div>
            <div className="agent-actions">
              <a href={`tel:${agent.phone || '05052149197'}`} className="action-btn call-btn">
                <Phone size={18} />
                <span>Aramak için tıklayın</span>
              </a>
              <a 
                href={`https://wa.me/${(agent.phone || '05052149197').replace(/\D/g, '')}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="action-btn message-btn"
              >
                <MessageCircle size={18} />
                <span>Mesaj Gönder</span>
              </a>
            </div>
          </div>
        </aside>
      </div>
      {/* Lightbox Overlay */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lightbox-overlay"
            onClick={() => setIsLightboxOpen(false)}
          >
            <button className="lightbox-close" onClick={() => setIsLightboxOpen(false)}>
              <X size={32} />
            </button>

            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <div className="lightbox-image-container">
                <AnimatePresence mode="wait">
                  <motion.img 
                    key={images[selectedImageIdx]}
                    initial={{ x: 100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -100, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    drag={isMobile ? "x" : false}
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.2}
                    onDragEnd={(e, { offset }) => {
                      const swipe = offset.x;
                      if (swipe < -50) {
                        handleNext();
                      } else if (swipe > 50) {
                        handlePrev();
                      }
                    }}
                    src={images[selectedImageIdx]} 
                    alt={title} 
                    className="lightbox-image" 
                  />
                </AnimatePresence>
                
                {images.length > 1 && (
                  <>
                    <button className="lightbox-nav-btn prev mobile-hidden" onClick={handlePrev}>
                      <ChevronLeft size={32} />
                    </button>
                    <button className="lightbox-nav-btn next mobile-hidden" onClick={handleNext}>
                      <ChevronRight size={32} />
                    </button>
                  </>
                )}
              </div>

              {images.length > 1 && (
                <div className="lightbox-thumbnails-wrapper">
                  <div className="thumbnails-container" ref={lightboxThumbnailsRef}>
                    {images.map((img, idx) => (
                      <div 
                        key={idx} 
                        className={`thumbnail-item ${idx === selectedImageIdx ? 'active' : ''}`}
                        onClick={() => setSelectedImageIdx(idx)}
                      >
                        <img src={img} alt={`${title} ${idx + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PropertyDetail;
